<script lang="ts">
    import EllipseLockup from '@amp/web-app-components/src/components/lockups/EllipseLockup/EllipseLockup.svelte';

    export let name: string;
    export let subtitle: string | null = null;
    export let hasArtwork: boolean;
    export let hasUberArtwork: boolean;
    export let hasVideoArtwork: boolean;
    export let hasFullWidthArtwork: boolean = false;
    export let headerStyle: string = '';
    export let showFavoriteButton: boolean = false;
    export let showShareButton: boolean = false;
    export let showClassicalButton: boolean = false;
</script>

<div
    data-testid="artist-detail-header"
    class="artist-header"
    class:artist-header--fixed={hasUberArtwork ||
        hasVideoArtwork ||
        hasFullWidthArtwork}
    class:artist-header--video={hasVideoArtwork}
    class:artist-header--full-width={hasFullWidthArtwork}
    class:artist-header--no-artwork={!hasArtwork &&
        !hasVideoArtwork &&
        !hasUberArtwork}
    style={headerStyle}
>
    {#if $$slots['video-artwork'] && hasVideoArtwork}
        <slot name="video-artwork" />
    {:else if $$slots['full-width-artwork'] && hasFullWidthArtwork}
        <slot name="full-width-artwork" />
    {:else if hasArtwork}
        <div class="artist-header__circular-artwork-container">
            <div class="artist-header__circular-artwork-gradient">
                <EllipseLockup
                    useDefaultContainerStyles={false}
                    artwork="slot"
                    title={null}
                    alt={name}
                >
                    <slot slot="artwork" name="artwork-gradient" />
                </EllipseLockup>
            </div>

            <div class="artist-header__circular-artwork">
                <EllipseLockup
                    useDefaultContainerStyles={false}
                    artwork="slot"
                    title={null}
                    alt={name}
                >
                    <slot slot="artwork" name="artwork" />
                </EllipseLockup>
            </div>
        </div>
    {/if}

    <div class="artist-header__name-container">
        {#if subtitle}
            <span data-testid="artist-subtitle" class="artist-header__metadata"
                >{subtitle}</span
            >
        {/if}

        <h1 data-testid="artist-header-name" class="artist-header__name">
            {name}
        </h1>

        {#if $$slots['classical-button'] && showClassicalButton}
            <span class="artist-header__classical-button">
                <slot name="classical-button" />
            </span>
        {/if}

        {#if $$slots['play-button']}
            <span class="artist-header__play-button">
                <slot name="play-button" />
            </span>
        {/if}

        {#if $$slots['favorited-badge'] && showFavoriteButton}
            <span class="artist-header__favorite-button">
                <slot name="favorited-badge" />
            </span>
        {/if}

        {#if $$slots['share-button'] && showShareButton}
            <span class="artist-header__share-button">
                <slot name="share-button" />
            </span>
        {/if}

        {#if $$slots['context-menu']}
            <span class="artist-header__context-menu">
                <slot name="context-menu" />
            </span>
        {/if}
    </div>
</div>

<style lang="scss">
    @use '@amp/web-shared-styles/sasskit-stylekit/ac-sasskit-config';
    @use 'ac-sasskit/core/selectors' as *;
    @use 'ac-sasskit/core/viewports' as *;
    @use 'ac-sasskit/core/types' as *;
    @use 'amp/stylekit/core/mixins/browser-targets' as *;
    @use 'amp/stylekit/core/mixins/materials' as *;
    @use 'amp/stylekit/core/mixins/line-clamp' as *;
    @use '@amp/web-shared-styles/app/core/globalvars' as *;
    @use '@amp/web-shared-styles/app/core/viewports' as *;
    @use '@amp/web-shared-styles/app/core/mixins/text-fade' as *;

    $fixed-artwork-max-width: viewport-get-property-for(xlarge, content);
    $artist-header-height: 386px;
    $artist-header-height-no-artwork: 110px;
    $artist-header-legibility-gradient: linear-gradient(
        to top,
        rgba(0, 0, 0, 0.36) 0%,
        rgba(0, 0, 0, 0.25) 36%,
        rgba(0, 0, 0, 0) 100%
    );
    $artist-header-legibility-gradient-height: 150px;
    $web-navigation-width-large: $global-sidebar-width-large;
    $web-navigation-width-fluid: #{unit-set($global-sidebar-ratio-to-page, vw)};

    .artist-header {
        position: relative;
        overflow: hidden;
        height: var(--artistHeaderHeightXs, #{$artist-header-height-xs});

        @media (--small) {
            height: $artist-header-height;
        }

        // Creates the background gradient
        &::after {
            content: '';
            display: block;
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            pointer-events: none;
            background: linear-gradient(
                transparent 0,
                var(--circularArtistHeaderGradientBottom)
            );
            z-index: var(--z-default);
        }
    }

    .artist-header--no-artwork {
        min-height: $artist-header-height-no-artwork;
        height: 100%;

        &::after,
        &::before {
            display: none;
        }

        // Prevent Grid overlap
        @media (--range-xsmall-down) {
            .artist-header__name-container {
                position: relative;
            }

            .artist-header__metadata {
                margin-top: 2px;
                grid-row: 2;
            }

            .artist-header__name {
                grid-row: 3 / 4;
            }
        }
    }

    .artist-header--fixed {
        // Creates the fixed header graphic
        &::before {
            content: '';
            width: calc(
                100vw -
                    var(
                        --sidebar-width-expanded,
                        #{$global-sidebar-width-large}
                    )
            );
            height: calc(
                (
                        100vw -
                            var(
                                --sidebar-width-expanded,
                                #{$global-sidebar-width-large}
                            )
                    ) /
                    var(
                        --fixed-artwork-ratio-lg,
                        #{$fixed-artwork-header-ratio-sm}
                    )
            );
            max-width: $fixed-artwork-max-width;
            max-height: calc(
                #{viewport-get-property-for(xlarge, content)} / var(--fixed-artwork-ratio-sm, #{$fixed-artwork-header-ratio-lg})
            );
            background-size: cover;
            background-image: none;
            background-color: transparent;
            background-position: center top;
            background-repeat: no-repeat;
            position: absolute;
            z-index: var(--z-default);
        }

        @media (--small) {
            max-height: calc(
                #{viewport-get-property-for(xlarge, content)} / var(--fixed-artwork-ratio-lg, #{$fixed-artwork-header-ratio-lg})
            );
            position: relative;
            height: calc(
                (
                        100vw -
                            var(
                                --sidebar-width-expanded,
                                #{$global-sidebar-width-large}
                            )
                    ) /
                    var(
                        --fixed-artwork-ratio-sm,
                        #{$fixed-artwork-header-ratio-sm}
                    )
            );
            z-index: var(--z-default);

            &::before {
                background-color: var(--background-color);
                background-image: var(--background-image-xs);
            }

            // Modifies the background gradient
            &::after {
                height: $artist-header-legibility-gradient-height;
                top: initial;
                background: $artist-header-legibility-gradient;
            }

            .artist-header__circular-artwork,
            .artist-header__circular-artwork-gradient {
                display: none;
            }
        }

        @media (--medium) {
            height: calc(
                (
                        100vw -
                            var(
                                --sidebar-width-expanded,
                                #{$global-sidebar-width-large}
                            )
                    ) /
                    var(
                        --fixed-artwork-ratio-lg,
                        #{$fixed-artwork-header-ratio-lg}
                    )
            );

            &::before {
                height: calc(
                    (
                            100vw -
                                var(
                                    --sidebar-width-expanded,
                                    #{$global-sidebar-width-large}
                                )
                        ) /
                        var(
                            --fixed-artwork-ratio-sm,
                            #{$fixed-artwork-header-ratio-lg}
                        )
                );
                background-image: var(--background-image);
            }
        }

        @include target-safari {
            @include feature-detect(is-refocused) {
                // Avoid a Safari painting issue, where artwork is not visible on refocus
                // Workaround for rdar://95827237 (Artwork not visible on window refocus on Apple Music Artist pages)
                will-change: opacity;
                opacity: 0.9999;
            }
        }
    }

    .artist-header--video {
        min-height: $artist-header-height-xs;

        // Set aspect ratios on AMWEB to spec, for smaller screens
        height: calc(
            (100vw - var(--web-navigation-width)) / #{$fixed-artwork-header-ratio-sm}
        );

        @media (--medium) {
            // Set aspect ratios on AMWEB to spec, for larger screens
            height: calc(
                (100vw - var(--web-navigation-width)) / #{$fixed-artwork-header-ratio-lg}
            );
            max-height: calc(
                #{viewport-get-property-for(xlarge, content)} / $fixed-artwork-header-ratio-lg
            );
        }

        @media (--sidebar-visible) {
            // overflow: visible; // Allow video to display above container and under web chrome
        }

        // Render legibility gradient in all viewports, when video is present
        &::after {
            height: $artist-header-legibility-gradient-height;
            top: initial;
            background: $artist-header-legibility-gradient;
        }
    }

    .artist-header__name-container {
        width: 100%;
        height: 100%;
        padding: 20px var(--bodyGutter) 23px;
        bottom: 0;
        position: absolute;
        display: grid;
        justify-content: end;
        align-items: end;
        // minmax guarantees minimum vertical space between play button and favorite/more on xsmall screens with no artist
        grid-template-rows: auto minmax(16px, 1fr) auto;
        grid-template-columns: 1fr auto;
        grid-template-areas:
            '. classical favorite menu'
            '. . . .' // blank row expands space between top and bottom rows
            'artist . play play';
        z-index: calc(var(--z-default) + 1);

        @include target-safari {
            // GPU layer to avoid painting issue with web chrome and material buttons
            transform: translate(0);
            // Intentionally avoiding GPU-specific z-index: var(--z-gpu) to unblock back button on mobile devices
        }

        @media (--small) {
            height: auto;
            grid-template-rows: auto;
            grid-template-columns: auto 1fr auto auto auto;
            grid-template-areas: 'play artist classical favorite menu';
        }
    }

    .artist-header__name {
        grid-area: artist;
        color: var(--artist-header-name-color, var(--systemPrimary));
        text-overflow: clip;
        user-select: text;
        cursor: text;
        font: var(--header-emphasized);

        @include line-clamp(2);
        @include text-fade;

        // Reset text fade in full width artist headers
        &:not(.artist-header__name--full-width) {
            @include text-fade;
        }

        .artist-header--fixed & {
            @media (--small) {
                --artist-header-name-color: white;
            }
        }

        .artist-header--video &,
        .artist-header--full-width & {
            --artist-header-name-color: white;
        }
    }

    // Allow text fade for viewports 1000px & above
    .artist-header__name--full-width {
        @media (--small) {
            @include text-fade;
        }
    }

    .artist-header__metadata {
        font: var(--callout-emphasized);
        color: var(--artist-header-metadata-color, var(--systemSecondary));
        text-transform: uppercase;
        margin-bottom: 3px;
        grid-row: 2;

        @media (--small) {
            grid-row: 1;
        }

        .artist-header--fixed & {
            @media (--small) {
                --artist-header-metadata-color: white;
            }
        }

        .artist-header--video &,
        .artist-header--full-width & {
            --artist-header-metadata-color: white;
        }
    }

    .artist-header__play-button {
        --playButtonSize: 36px;
        width: var(--playButtonSize);
        height: var(--playButtonSize);
        grid-area: play;
        justify-self: end;

        // vertically center play button with bottom line of artist name
        align-self: end;
        transform: translateY(-1px);

        :global(button) {
            border-radius: 50%;
        }

        :global(button:not(:focus)) {
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
        }

        // Needs higher specificity than play-button styles
        :global(button.play-button svg.icon) {
            // Suppress material hairline from bleeding out from behind hovered platter
            background-color: transparent;
            backdrop-filter: none;
        }

        :global(button:disabled) {
            opacity: 0.35; // Need to use `opacity` for system-accent colors
        }

        @media (--small) {
            margin-inline-end: 16px;
        }
    }

    $circular-artwork-size: 190px;
    $circular-artwork-size-xs: 174px;
    $circular-artwork-top-offset: 80px;
    $circular-artwork-top-offset-xs: 40px;

    .artist-header__circular-artwork-container {
        padding-top: $circular-artwork-top-offset-xs;
        width: 100%;
        position: relative;
        display: grid;
        grid-template-columns: 100%;
        justify-items: center;
        z-index: calc(var(--z-default) + 1);

        @media (--small) {
            padding-top: $circular-artwork-top-offset;
        }
    }

    .artist-header__circular-artwork,
    .artist-header__circular-artwork-gradient {
        width: $circular-artwork-size-xs;
        height: $circular-artwork-size-xs;
        // Places artwork containers on top of each other without using absolute positioning
        grid-column: 1 / -1;
        grid-row: 1 / -1;
        border-radius: 50%;
        // box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);

        @media (--small) {
            width: $circular-artwork-size;
            height: $circular-artwork-size;
        }
    }

    .artist-header__circular-artwork {
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
        border-radius: 50%;
    }

    .artist-header__circular-artwork-gradient {
        opacity: 0.4;
        border-radius: 50%;
        filter: blur(40px);
        transform: translateY(10px) translateZ(0);
    }

    .artist-header__favorite-button,
    .artist-header__share-button,
    .artist-header__context-menu {
        --contextMenuButtonSize: #{$action-button-size};
        line-height: 0;
        z-index: calc(var(--z-default) + 1);
    }

    .artist-header__share-button {
        --shareButtonBackground: var(--systemQuinary);
        --shareIconColor: var(--keyColor);
        width: $action-button-size;
        height: $action-button-size;
        border-radius: 50%;

        // We currently never have both share button *and* a context menu, so it's
        // safe to render the share button in the menu area for now. If this assumption
        // ever changes, the share button will need its own grid area.
        grid-area: menu;

        @mixin material-share-button {
            --shareButtonBackground: transparent;
            --shareIconColor: white;

            @include system-standard-thin-material(dark);
        }

        .artist-header--fixed & {
            @media (--small) {
                @include material-share-button;
            }
        }

        .artist-header--video &,
        .artist-header--full-width & {
            @include material-share-button;
        }
    }

    .artist-header__favorite-button {
        margin-inline-start: 8px;
        grid-area: favorite;
        border-radius: 50%;

        // Include extra space between button and menu on small touchscreens
        // TODO: rdar://106778012 (Expand the contextual menu button "..." tap target on mobile)
        @media (--range-xsmall-down) {
            @media (--target-touchscreen) {
                $touch-target-padding: calc((44px - $action-button-size) / 2);
                margin-inline-end: $touch-target-padding;
            }
        }

        // Favorite button material color overrides
        @mixin material-favorite-button {
            // Unfavorited overrides
            --favoriteButtonBackground: transparent;
            --favoriteButtonStarOutline: white;

            // Favorited overrides
            --favoriteButtonStarCutout: white;
            --favoriteButtonStarFill: transparent;

            // Hover overrides
            --favoriteButtonBackground-hover: transparent;
            --favoriteButtonStarCutout-hover: white;
            --favoriteButtonStarFill-hover: transparent;

            @include system-standard-thin-material(dark);
        }

        .artist-header--fixed & {
            @media (--small) {
                @include material-favorite-button;
            }
        }

        .artist-header--video &,
        .artist-header--full-width & {
            @include material-favorite-button;
        }
    }

    .artist-header__context-menu {
        margin-inline-start: 8px;
        grid-area: menu;
        border-radius: 50%;

        :global(.more-button) {
            --contextMenuCircleFillOverride: var(--systemQuinary);
            --contextMenuEllipsisFillOverride: var(--keyColor);
            --contextMenuCircleHoverFill: var(--systemQuinary);
            --contextMenuEllipsisHoverFill: var(--keyColor);
            background-color: transparent; // Reset material for when circular artwork is visible
            backdrop-filter: none; // Reset material for when circular artwork is visible

            @mixin material-context-button {
                --contextMenuCircleFillOverride: transparent;
                --contextMenuEllipsisFillOverride: white;
                --contextMenuCircleHoverFill: transparent;
                --contextMenuEllipsisHoverFill: white;

                @include system-standard-thin-material(dark);
            }

            .artist-header--fixed & {
                @media (--small) {
                    @include material-context-button;
                }
            }

            .artist-header--video &,
            .artist-header--full-width & {
                @include material-context-button;
            }
        }
    }

    .artist-header__classical-button {
        grid-area: classical;
        width: $action-button-size;
        height: $action-button-size;
        border-radius: 50%;
        background: var(--systemQuinary);

        &:hover,
        &:focus-visible {
            background: var(--keyColor);

            :global(svg) {
                fill: white;
            }
        }

        :global(.click-action) {
            display: block;
        }

        :global(svg) {
            fill: var(--keyColor);
        }

        .artist-header--fixed & {
            @media (--small) {
                @include system-standard-thin-material(dark);

                :global(svg) {
                    fill: white;
                }
            }
        }

        .artist-header--video &,
        .artist-header--full-width & {
            @include system-standard-thin-material(dark);

            :global(svg) {
                fill: white;
            }
        }
    }
</style>
